import React, { useState } from 'react';
import { cn } from '@/lib/utils';
import { SegmentedControl } from '@/components/ui/segmented-control';
import { AnimatedCollapse } from '@/components/ui/animated-collapse';
import type { PayBucket } from '@/utils/rateCalculations';
import { BucketBreakdown } from './BucketBreakdown';
import { RoleCyclerButton } from './RoleCyclerButton';
import { SurchargeDeltaRow } from '../surcharge/SurchargeDeltaRow';
import { RateBadge } from './RateBadge';
import { MethodNavGroup } from '../MethodNavGroup';
import { RestGapConnector } from './RestGapConnector';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export interface ShiftData {
  label: string;
  workHours: number;
  startRate: string;
  endRate?: string;
  basePay: number;
  surcharged: boolean;
  surchargedPay?: number;
  surchargeAmount?: number;
  baseBuckets?: PayBucket[];
  surchargedBuckets?: PayBucket[];
  roleContext?: string;
}

export interface RestConnector {
  hours: number;
  shortedHours?: number;
  triggered: boolean;
  previousEndRate?: string;
  nextShiftRate?: string;
}

export interface MethodTimeline {
  methodName: string;
  methodValue: string;
  helpFieldName?: string;
  shifts: ShiftData[];
  connectors: RestConnector[];
  subBadgeOptions?: { label: string; value: string }[];
  activeSubBadgeValue?: string;
  onSubBadgeSelect?: (value: string) => void;
}

interface ShiftTimelineComparisonProps {
  shifts: ShiftData[];
  connectors: RestConnector[];
  methodName: string;
  allMethods?: MethodTimeline[];
  onMethodChange?: (methodValue: string) => void;
  roleOptions?: string[];
  activeRoleIndex?: number;
  onRoleCycle?: () => void;
  showMethodHeader?: boolean;
  compactFirstShift?: boolean;
  showCollapsedDuration?: boolean;
  showExpandHint?: boolean;
  /** Optional text shown in expanded surcharged cards next to Shift Length */
  surchargePeriodLabel?: string;
  /** Optional text used for triggered rest-gap divider labels */
  triggeredConnectorLabel?: string;
  /** Content rendered at the bottom of the timeline container */
  footer?: React.ReactNode;
  /** Optional callout rendered inside expanded surcharged shift cards */
  expandedSurchargedShiftCallout?: React.ReactNode;
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

/* ------------------------------------------------------------------ */
/*  SurchargedShiftCard                                                */
/* ------------------------------------------------------------------ */

const SurchargedShiftCard: React.FC<{
  shift: ShiftData;
  expanded: boolean;
  onToggle: () => void;
  onRoleCycle?: () => void;
  roleKey?: string | number;
  showCollapsedDuration: boolean;
  showExpandHint: boolean;
  surchargePeriodLabel?: string;
  expandedCallout?: React.ReactNode;
}> = ({ shift, expanded, onToggle, onRoleCycle, roleKey, showCollapsedDuration, showExpandHint, surchargePeriodLabel, expandedCallout }) => {
  const centeredInlineHint = showExpandHint;
  const hasCrowdedRateLabels =
    centeredInlineHint && (shift.startRate.length > 24 || (shift.endRate?.length ?? 0) > 24);
  const surchargeBadge = (
    <span
      className={cn(
        'inline-flex items-center rounded-[0.25rem] px-2 py-1',
        'font-heading text-[10px] sm:text-[12px] uppercase tracking-[1px] leading-none font-bold',
        'bg-primary/20 text-primary-text dark:bg-primary dark:text-primary-foreground',
      )}
    >
      Short Turn
    </span>
  );

  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={expanded}
      aria-label={expanded ? 'Hide surcharge breakdown' : 'Show surcharge breakdown'}
      className={cn(
        'w-full text-left rounded-[0.25rem] border border-primary/45',
        'bg-primary/[0.04] shadow-[0_0_8px_rgba(232,134,12,0.1)]',
        'min-h-[44px] sm:min-h-0 px-3 py-2 cursor-pointer motion-safe:transition-colors',
        'hover:bg-primary/[0.07]',
        '[border-top-color:rgba(232,134,12,0.35)]',
      )}
    >
      <div
        key={`${shift.startRate}-${shift.endRate ?? ''}`}
        className={cn(
          centeredInlineHint
            ? 'grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2'
            : 'flex justify-between gap-2',
          'mt-0',
          roleKey != null && 'motion-safe:animate-[fadeSlideIn_0.2s_ease-out]',
        )}
      >
        <div className="flex items-center gap-2 min-w-0 justify-self-start">
          <span className="font-heading text-[10px] sm:text-[12px] leading-none uppercase tracking-[1px] text-foreground/90">
            {shift.label}
          </span>
          {showCollapsedDuration && (
            <span className="font-mono text-[10px] sm:text-[12px] leading-none text-foreground/70">
              {shift.workHours}h
            </span>
          )}
          {surchargeBadge}
        </div>

        {centeredInlineHint && (
          <div
            className={cn(
              'flex items-center justify-center px-1',
              hasCrowdedRateLabels
                ? 'self-stretch min-h-[38px] sm:min-h-[44px] py-3 sm:py-4'
                : 'min-h-[22px] py-2',
            )}
          >
            <span className="font-body text-[10px] sm:text-[12px] leading-none text-muted-foreground/45 text-center whitespace-nowrap">
              <span className="sm:hidden">{expanded ? 'Collapse' : 'Expand'}</span>
              <span className="hidden sm:inline">{expanded ? 'Collapse' : 'Expand'}</span>
            </span>
          </div>
        )}

        <div className={cn(
          'flex flex-col items-end shrink-0 justify-self-end',
          hasCrowdedRateLabels ? 'gap-5' : 'gap-2',
        )}>
          <div className="flex items-center gap-1">
            <span
              className="font-label leading-[18px] uppercase tracking-[0.5px]"
              style={{ fontSize: 8, color: 'color-mix(in oklab, var(--color-muted-foreground) 45%, transparent)' }}
            >
              Starts
            </span>
            <RateBadge label={shift.startRate} elevated />
          </div>
          {shift.endRate && (
            <div className="flex items-center gap-1">
              <span
                className="font-label leading-[18px] uppercase tracking-[0.5px]"
                style={{ fontSize: 8, color: 'color-mix(in oklab, var(--color-muted-foreground) 45%, transparent)' }}
              >
                Ends
              </span>
              <RateBadge label={shift.endRate} endTier />
            </div>
          )}
        </div>
      </div>

      <AnimatedCollapse open={expanded}>
        <div className="mt-2">
          {!showCollapsedDuration && (
            <div className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-1">
              <div className="flex items-center gap-2">
                <span className="font-label text-[10px] sm:text-[12px] uppercase tracking-[0.5px] text-muted-foreground/45">
                  Shift Length
                </span>
                <span className="font-mono text-[10px] sm:text-[12px] leading-none text-foreground/75">
                  {shift.workHours}h
                </span>
              </div>
              {surchargePeriodLabel && (
                <div className="flex items-center gap-2">
                  <span className="font-label text-[10px] sm:text-[12px] uppercase tracking-[0.5px] text-muted-foreground/45">
                    Surcharge Period
                  </span>
                  <span className={cn(
                    'font-mono text-[10px] sm:text-[12px] leading-none',
                    surchargePeriodLabel.toLowerCase().includes('short') ? 'text-destructive-text' : 'text-primary-text/85',
                  )}>
                    {surchargePeriodLabel}
                  </span>
                </div>
              )}
            </div>
          )}
          {shift.roleContext && (
            <RoleCyclerButton
              roleContext={shift.roleContext}
              onRoleCycle={onRoleCycle}
              roleKey={roleKey}
            />
          )}

          {shift.baseBuckets && shift.baseBuckets.length > 0 && (
            <>
              <span className="font-label text-[10px] sm:text-[12px] uppercase tracking-[0.5px] text-muted-foreground/45 block mb-1">
                Normal Pay
              </span>
              <BucketBreakdown
                buckets={shift.baseBuckets}
                totalLabel="Base Pay"
                totalAmount={shift.basePay}
                variant="normal"
                roleKey={roleKey}
              />
            </>
          )}

          {shift.surchargedBuckets && shift.surchargedBuckets.length > 0 && (
            <>
              <span className="font-label text-[10px] sm:text-[12px] uppercase tracking-[0.5px] text-primary-text block mb-1">
                With Short Turn Surcharge
              </span>
              <BucketBreakdown
                buckets={shift.surchargedBuckets}
                totalLabel="Adjusted Pay"
                totalAmount={shift.surchargedPay ?? shift.basePay}
                variant="surcharged"
                roleKey={roleKey}
              />
            </>
          )}

          {shift.surchargeAmount != null && shift.surchargedPay != null && (
            <SurchargeDeltaRow
              surchargedPay={shift.surchargedPay}
              basePay={shift.basePay}
              surchargeAmount={shift.surchargeAmount}
            />
          )}

          {expandedCallout}
        </div>
      </AnimatedCollapse>
    </button>
  );
};

/* ------------------------------------------------------------------ */
/*  ShiftCard                                                          */
/* ------------------------------------------------------------------ */

const ShiftCard: React.FC<{
  shift: ShiftData;
  expanded: boolean;
  onToggle: () => void;
  onRoleCycle?: () => void;
  roleKey?: string | number;
  showCollapsedDuration: boolean;
  showExpandHint: boolean;
  surchargePeriodLabel?: string;
  expandedCallout?: React.ReactNode;
}> = ({ shift, expanded, onToggle, onRoleCycle, roleKey, showCollapsedDuration, showExpandHint, surchargePeriodLabel, expandedCallout }) => {
  if (shift.surcharged) {
    return (
      <SurchargedShiftCard
        shift={shift}
        expanded={expanded}
        onToggle={onToggle}
        onRoleCycle={onRoleCycle}
        roleKey={roleKey}
        showCollapsedDuration={showCollapsedDuration}
        showExpandHint={showExpandHint}
        surchargePeriodLabel={surchargePeriodLabel}
        expandedCallout={expandedCallout}
      />
    );
  }

  return (
    <div
      className={cn(
        'w-full rounded-[0.25rem] border border-border/60 bg-surface-2',
        'opacity-70 px-3 py-2',
        '[border-top-color:rgba(255,255,255,0.06)]',
      )}
    >
      <div className="flex items-baseline gap-2">
        <span className="font-heading text-[10px] sm:text-[12px] leading-none uppercase tracking-[1px] text-foreground/70 shrink-0">
          {shift.label}
        </span>
        {showCollapsedDuration && (
          <span className="font-mono text-[10px] sm:text-[12px] leading-none text-muted-foreground/45 shrink-0">
            {shift.workHours}h
          </span>
        )}
      </div>

      <div className="flex items-center justify-between mt-2">
        <div className="flex items-center gap-1">
          <span className="font-label text-[10px] sm:text-[12px] leading-[18px] uppercase tracking-[0.5px] text-muted-foreground/30">Starts</span>
          <RateBadge label={shift.startRate} />
        </div>
        {shift.endRate && (
          <div className="flex items-center gap-1">
            <span className="font-label text-[10px] sm:text-[12px] leading-[18px] uppercase tracking-[0.5px] text-muted-foreground/30">Ends</span>
            <RateBadge label={shift.endRate} />
          </div>
        )}
      </div>
    </div>
  );
};

const ReferenceShiftStrip: React.FC<{ shift: ShiftData; showCollapsedDuration: boolean }> = ({ shift, showCollapsedDuration }) => (
  <div
    className={cn(
      'w-full rounded-[0.25rem] border border-border/60 bg-surface-2/40',
      'opacity-80 px-2 py-2',
      '[border-top-color:rgba(255,255,255,0.06)]',
    )}
  >
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-baseline gap-2">
        <span className="font-heading text-[10px] sm:text-[12px] leading-none uppercase tracking-[1px] text-foreground/65">
          {shift.label}
        </span>
        {showCollapsedDuration && (
          <span className="font-mono text-[10px] sm:text-[12px] leading-none text-muted-foreground/45">
            {shift.workHours}h
          </span>
        )}
      </div>
      {shift.endRate && (
        <div className="flex items-center gap-1 shrink-0">
          <span className="font-label text-[10px] sm:text-[12px] leading-none uppercase tracking-[0.5px] text-muted-foreground/35">
            Ends
          </span>
          <RateBadge label={shift.endRate} />
        </div>
      )}
    </div>
  </div>
);

/* ------------------------------------------------------------------ */
/*  RestConnectorElement                                               */
/* ------------------------------------------------------------------ */

const RestConnectorHorizontal: React.FC<{
  connector: RestConnector;
  triggeredConnectorLabel?: string;
}> = ({ connector, triggeredConnectorLabel }) => (
  <RestGapConnector
    hours={connector.hours}
    labelText={connector.triggered
      ? (triggeredConnectorLabel ?? (connector.shortedHours != null ? `${connector.shortedHours}hr Shorted` : undefined))
      : undefined}
    triggered={connector.triggered}
    subLabel={!connector.triggered && connector.nextShiftRate ? 'No Surcharge' : undefined}
  />
);

/* ------------------------------------------------------------------ */
/*  Main Component                                                     */
/* ------------------------------------------------------------------ */

export const ShiftTimelineComparison: React.FC<ShiftTimelineComparisonProps> = ({
  shifts: defaultShifts,
  connectors: defaultConnectors,
  methodName: defaultMethodName,
  allMethods,
  onMethodChange,
  roleOptions,
  activeRoleIndex,
  onRoleCycle,
  showMethodHeader = true,
  compactFirstShift = false,
  showCollapsedDuration = true,
  showExpandHint = false,
  surchargePeriodLabel,
  triggeredConnectorLabel,
  footer,
  expandedSurchargedShiftCallout,
}) => {
  const [expandedShiftIndex, setExpandedShiftIndex] = useState<number | null>(null);
  const [activeMethodIndex, setActiveMethodIndex] = useState(() =>
    allMethods ? Math.max(0, allMethods.findIndex(m => m.methodName === defaultMethodName)) : 0,
  );

  const toggleShift = (index: number) => {
    setExpandedShiftIndex((prev) => (prev === index ? null : index));
  };

  const handleImmediateSelect = (methodValue: string) => {
    const idx = allMethods?.findIndex(m => m.methodValue === methodValue) ?? -1;
    if (idx >= 0) {
      setActiveMethodIndex(idx);
      setExpandedShiftIndex(null);
    }
  };

  const activeTimeline = allMethods?.[activeMethodIndex];
  const shifts = activeTimeline?.shifts ?? defaultShifts;
  const connectors = activeTimeline?.connectors ?? defaultConnectors;
  const methodName = activeTimeline?.methodName ?? defaultMethodName;

  // Composite key that triggers fadeSlideIn on role OR sub-badge changes
  const contentAnimKey = activeTimeline?.activeSubBadgeValue
    ? `${activeRoleIndex ?? 0}-${activeTimeline.activeSubBadgeValue}`
    : activeRoleIndex;

  const renderSubBadgeSelector = () => {
    if (!activeTimeline?.subBadgeOptions || activeTimeline.subBadgeOptions.length <= 1 || !activeTimeline.onSubBadgeSelect) {
      return null;
    }

    const opts = activeTimeline.subBadgeOptions;
    const resolvedValue = opts.some((o) => o.value === activeTimeline.activeSubBadgeValue)
      ? activeTimeline.activeSubBadgeValue!
      : opts[0]?.value ?? '';

    // Overflow fallback: >5 options would cramp SegmentedControl
    if (opts.length > 5) {
      return (
        <div className="flex items-center gap-1 flex-wrap justify-center">
          {opts.map((opt) => {
            const isActive = opt.value === resolvedValue;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={(e) => { e.stopPropagation(); activeTimeline.onSubBadgeSelect?.(opt.value); }}
                className={cn(
                  'inline-flex items-center rounded-[0.25rem] border px-2 py-1 font-mono text-[10px] sm:text-[12px] font-semibold leading-none',
                  'motion-safe:transition-[color,background-color,border-color,opacity] motion-safe:duration-200',
                  isActive
                    ? 'border-primary/20 bg-primary/10 text-primary-text'
                    : 'border-border/30 bg-transparent text-muted-foreground/35 hover:text-muted-foreground/55 hover:border-border/50 cursor-pointer',
                )}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      );
    }

    return (
      <SegmentedControl
        ariaLabel="Method variant"
        options={opts.map((o) => ({ value: o.value, label: o.label }))}
        value={resolvedValue}
        onChange={(v) => activeTimeline.onSubBadgeSelect?.(v)}
        size="sm"
        className="w-auto"
      />
    );
  };

  if (shifts.length === 0) return null;

  return (
    <div className="mt-1 mb-2">
      {showMethodHeader && (
        <div className="flex flex-col items-center gap-1 mb-2">
          {allMethods ? (
            <MethodNavGroup
              methods={allMethods}
              activeMethodName={methodName}
              onMethodChange={onMethodChange}
              onImmediateSelect={handleImmediateSelect}
            />
          ) : (
            <span className="inline-flex items-center rounded-[0.25rem] border border-primary/20 bg-primary/10 px-2 py-1 font-mono text-[10px] sm:text-[12px] font-semibold text-primary-text">
              {methodName}
            </span>
          )}

          {/* Sub-badge options for active method (e.g. fixed start multiplier selector) */}
          {renderSubBadgeSelector()}
        </div>
      )}

      {/* Timeline */}
      <div className="w-full">
        <div className="flex flex-col gap-0">
          {shifts.map((shift, idx) => (
            <React.Fragment key={`${methodName}-${shift.label}`}>
              {compactFirstShift && idx === 0 && !shift.surcharged ? (
                <ReferenceShiftStrip shift={shift} showCollapsedDuration={showCollapsedDuration} />
              ) : (
                <ShiftCard
                  shift={shift}
                  expanded={expandedShiftIndex === idx}
                  onToggle={() => toggleShift(idx)}
                  onRoleCycle={onRoleCycle}
                  roleKey={contentAnimKey}
                  showCollapsedDuration={showCollapsedDuration}
                  showExpandHint={showExpandHint}
                  surchargePeriodLabel={surchargePeriodLabel}
                  expandedCallout={expandedSurchargedShiftCallout}
                />
              )}
              {idx < connectors.length && (
                <RestConnectorHorizontal
                  connector={connectors[idx]}
                  triggeredConnectorLabel={triggeredConnectorLabel}
                />
              )}
            </React.Fragment>
          ))}
        </div>
        {footer}
      </div>

    </div>
  );
};
